import { demoDetails, demoSearch } from "@/lib/demo-data";
import type { JobDetail, JobSummary, SearchResponse } from "@/lib/types";

const PUBLIC_JOB_LIMIT = 500;

export interface LocationSuggestion {
  id: string;
  type: "city" | "town" | "state" | string;
  name: string;
  state?: string;
  country: string;
  country_code: string;
  display_name: string;
}

export async function searchJobs({
  query = "",
  location = "",
  limit = 12,
  offset = 0,
  signal,
}: {
  query?: string;
  location?: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
} = {}): Promise<SearchResponse> {
  const safeLimit = Math.min(Math.max(limit, 1), 20);
  const safeOffset = Math.min(Math.max(offset, 0), PUBLIC_JOB_LIMIT - 1);
  const params = new URLSearchParams({
    q: query.slice(0, 120),
    location: location.slice(0, 120),
    limit: String(safeLimit),
    offset: String(safeOffset),
    __chuno_search: "3",
  });

  try {
    const response = await fetch(`/api/jobs?${params}`, { signal });
    if (!response.ok) throw new Error(`Search failed (${response.status})`);
    const data = (await response.json()) as SearchResponse;
    return {
      ...data,
      estimatedTotalHits: Math.min(PUBLIC_JOB_LIMIT, Math.max(0, data.estimatedTotalHits || 0)),
      ...(typeof data.nbHits === "number" ? { nbHits: Math.min(PUBLIC_JOB_LIMIT, Math.max(0, data.nbHits)) } : {}),
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return demoSearch(query, location, safeLimit, safeOffset);
  }
}

export async function getJob(id: string, signal?: AbortSignal): Promise<JobDetail | null> {
  const injected = window.__CHUNO_JOB__;
  if (injected?.job_id === id) return injected;
  if (!/^[a-zA-Z0-9_-]{3,80}$/.test(id)) return null;

  try {
    const response = await fetch(`/api/jobs/${encodeURIComponent(id)}`, { signal });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Job lookup failed (${response.status})`);
    return (await response.json()) as JobDetail;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return demoDetails[id] || null;
  }
}

export async function getJobCount(signal?: AbortSignal): Promise<number> {
  try {
    const response = await fetch("/api/jobs/count", { signal });
    if (!response.ok) throw new Error(`Count failed (${response.status})`);
    const data = (await response.json()) as { count?: number };
    return Number.isSafeInteger(data.count) ? Math.min(PUBLIC_JOB_LIMIT, Math.max(0, data.count as number)) : PUBLIC_JOB_LIMIT;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return PUBLIC_JOB_LIMIT;
  }
}

export async function suggestLocations(query: string, signal?: AbortSignal): Promise<LocationSuggestion[]> {
  const value = query.trim().slice(0, 80);
  if (!value) return [];

  const response = await fetch(`/api/locations/suggest?q=${encodeURIComponent(value)}&limit=8`, { signal });
  if (!response.ok) throw new Error(`Location suggestions failed (${response.status})`);
  const data = (await response.json()) as { suggestions?: LocationSuggestion[] };
  return Array.isArray(data.suggestions) ? data.suggestions : [];
}

declare global {
  interface Window {
    __CHUNO_JOB__?: JobDetail;
    __CHUNO_RELATED__?: JobSummary[];
    __CHUNO_HOME__?: {
      jobs: SearchResponse["hits"];
      count: number;
    };
  }
}
